import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { RefreshCw, AlertCircle, ArrowLeft, Users, TrendingUp, Calendar, CreditCard, Mail, X, Copy, Check, Gift, Send } from 'lucide-react';
import { fetchPremiumStatusData, PremiumStatus, PremiumStatusMetrics, subscribeToPremiumStatusUpdates } from '../services/firebaseService';
import Alert, { AlertDescription } from '../components/ui/alert';

interface EmailTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  subscriber: PremiumStatus | null;
  onSent?: (uuid: string, type: 'feedback' | 'winback') => void;
}

const EmailTemplateModal: React.FC<EmailTemplateModalProps> = ({ isOpen, onClose, subscriber, onSent }) => {
  const [copied, setCopied] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [subject, setSubject] = useState('Thanks for using Notely');
  const [body, setBody] = useState('');
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const firstName = (subscriber?.name || '').split(' ')[0];
    const greeting = firstName ? `Hi ${firstName},` : 'Hi,';
    const platform = subscriber?.platform;

    let body: string;
    if (platform === 'iOS') {
      body = `${greeting}

Thanks for purchasing Notely! I built it to make transcription faster and less of a hassle. I hope it is already saving you time.

If you are enjoying it, a review on the App Store would mean a lot:
https://apps.apple.com/se/app/notely-ai/id6740462619?l=en-GB&platform=iphone

Have feedback or a feature idea? Reply directly to this email. I read every message.

Kind regards
Christopher State`;
    } else if (platform === 'Android') {
      body = `${greeting}

Thanks for purchasing Notely! I built it to make transcription faster and less of a hassle. I hope it is already saving you time.

If you are enjoying it, a review on the Google Play would mean a lot:
https://play.google.com/store/apps/details?id=com.cstate.notelyapp&hl=en

Have feedback or a feature idea? Reply directly to this email. I read every message.

Kind regards
Christopher State`;
    } else {
      body = `${greeting}

Thanks for purchasing Notely! I built it to make transcription faster and less of a hassle. I hope it is already saving you time.

If you are enjoying it, a review on the Mac App Store would mean a lot:
https://apps.apple.com/se/app/notely-ai/id6740462619?l=en-GB&platform=mac

Have feedback or a feature idea? Reply directly to this email. I read every message.

Kind regards
Christopher State`;
    }

    setSubject('Thanks for using Notely');
    setBody(body);
    setSent(false);
    setSendError(null);
  }, [subscriber]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(body);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
      const el = document.createElement('textarea');
      el.value = body;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleSend = async () => {
    if (!subscriber?.email) return;
    setSending(true);
    setSendError(null);
    try {
      const res = await fetch('https://api.notely.se/dashboard/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: subscriber.email, subject, body, type: 'feedback', uuid: subscriber.uuid }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to send');
      }
      onSent?.(subscriber.uuid, 'feedback');
      setSent(true);
      setTimeout(() => setSent(false), 3000);
    } catch (err) {
      setSendError(err instanceof Error ? err.message : 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === overlayRef.current) onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4"
      onClick={handleOverlayClick}
    >
      <div className="bg-gray-800 rounded-lg w-full max-w-lg shadow-xl">
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-blue-400" />
            <h2 className="text-white font-semibold">Feedback Request Email</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div className="bg-gray-700 rounded p-3">
            <p className="text-xs text-gray-400 mb-1">To:</p>
            <p className="text-white text-sm font-mono">
              {subscriber?.email || <span className="text-gray-400 italic">No email available</span>}
            </p>
          </div>

          <div className="bg-gray-700 rounded p-3">
            <p className="text-xs text-gray-400 mb-1">Subject:</p>
            <input
              type="text"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              className="w-full bg-transparent text-white text-sm font-mono outline-none"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-gray-400">Body:</p>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-600 hover:bg-gray-500 text-white text-xs rounded transition-colors"
                >
                  {copied ? (
                    <>
                      <Check className="w-3.5 h-3.5" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="w-3.5 h-3.5" />
                      Copy
                    </>
                  )}
                </button>
                <button
                  onClick={handleSend}
                  disabled={!subscriber?.email || sending || sent}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs rounded transition-colors"
                >
                  {sending ? (
                    <>
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      Sending…
                    </>
                  ) : sent ? (
                    <>
                      <Check className="w-3.5 h-3.5" />
                      Sent!
                    </>
                  ) : (
                    <>
                      <Send className="w-3.5 h-3.5" />
                      Send
                    </>
                  )}
                </button>
              </div>
            </div>
            {sendError && (
              <p className="text-red-400 text-xs mb-2">{sendError}</p>
            )}
            <textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              rows={10}
              className="w-full bg-gray-900 text-gray-100 text-sm font-mono rounded p-4 whitespace-pre-wrap leading-relaxed resize-y outline-none border border-transparent focus:border-gray-600"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

interface WinBackEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  subscriber: PremiumStatus | null;
  onSent?: (uuid: string, type: 'feedback' | 'winback') => void;
}

const WinBackEmailModal: React.FC<WinBackEmailModalProps> = ({ isOpen, onClose, subscriber, onSent }) => {
  const [copied, setCopied] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [subject, setSubject] = useState('A personal note from Notely');
  const [body, setBody] = useState('');
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const firstName = (subscriber?.name || '').split(' ')[0];
    const greeting = firstName ? `Hi ${firstName},` : 'Hi,';
    const platform = subscriber?.platform;

    let platformPhrase: string;
    if (platform === 'iOS') {
      platformPhrase = 'on iOS';
    } else if (platform === 'Android') {
      platformPhrase = 'on Android';
    } else {
      platformPhrase = 'on Mac';
    }

    setSubject('A personal note from Notely');
    setBody(`${greeting}

I noticed your Notely premium subscription has expired and wanted to reach out personally.

I'd love to know: was there something that didn't work for you ${platformPhrase}, or a reason you decided not to renew? Any feedback would mean a lot to me as I continue improving the app.

As a thank-you for your time, I'd like to offer you 3 months of premium access completely free. No strings attached. Just reply to this email and I'll sort it out for you.

Hope to hear from you!

Kind regards`);
    setSent(false);
    setSendError(null);
  }, [subscriber]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(body);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const el = document.createElement('textarea');
      el.value = body;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleSend = async () => {
    if (!subscriber?.email) return;
    setSending(true);
    setSendError(null);
    try {
      const res = await fetch('https://api.notely.se/dashboard/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: subscriber.email, subject, body, type: 'winback', uuid: subscriber.uuid }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to send');
      }
      onSent?.(subscriber.uuid, 'winback');
      setSent(true);
      setTimeout(() => setSent(false), 3000);
    } catch (err) {
      setSendError(err instanceof Error ? err.message : 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === overlayRef.current) onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4"
      onClick={handleOverlayClick}
    >
      <div className="bg-gray-800 rounded-lg w-full max-w-lg shadow-xl">
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div className="flex items-center gap-2">
            <Gift className="w-5 h-5 text-amber-400" />
            <h2 className="text-white font-semibold">Win-Back Offer Email</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div className="bg-gray-700 rounded p-3">
            <p className="text-xs text-gray-400 mb-1">To:</p>
            <p className="text-white text-sm font-mono">
              {subscriber?.email || <span className="text-gray-400 italic">No email available</span>}
            </p>
          </div>

          <div className="bg-gray-700 rounded p-3">
            <p className="text-xs text-gray-400 mb-1">Subject:</p>
            <input
              type="text"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              className="w-full bg-transparent text-white text-sm font-mono outline-none"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-gray-400">Body:</p>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-600 hover:bg-gray-500 text-white text-xs rounded transition-colors"
                >
                  {copied ? (
                    <>
                      <Check className="w-3.5 h-3.5" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="w-3.5 h-3.5" />
                      Copy
                    </>
                  )}
                </button>
                <button
                  onClick={handleSend}
                  disabled={!subscriber?.email || sending || sent}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs rounded transition-colors"
                >
                  {sending ? (
                    <>
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      Sending…
                    </>
                  ) : sent ? (
                    <>
                      <Check className="w-3.5 h-3.5" />
                      Sent!
                    </>
                  ) : (
                    <>
                      <Send className="w-3.5 h-3.5" />
                      Send
                    </>
                  )}
                </button>
              </div>
            </div>
            {sendError && (
              <p className="text-red-400 text-xs mb-2">{sendError}</p>
            )}
            <textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              rows={10}
              className="w-full bg-gray-900 text-gray-100 text-sm font-mono rounded p-4 whitespace-pre-wrap leading-relaxed resize-y outline-none border border-transparent focus:border-gray-600"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const formatRelative = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return `${days}d ago`;
};

const Subscriptions: React.FC = () => {
  const [premiumMetrics, setPremiumMetrics] = useState<PremiumStatusMetrics | null>(null);
  const [subscribers, setSubscribers] = useState<PremiumStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [timePeriod, setTimePeriod] = useState('month');
  const [isMobile, setIsMobile] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [selectedSubscriber, setSelectedSubscriber] = useState<PremiumStatus | null>(null);
  const [winBackModalOpen, setWinBackModalOpen] = useState(false);
  const [selectedWinBackSubscriber, setSelectedWinBackSubscriber] = useState<PremiumStatus | null>(null);
  const [recentExpanded, setRecentExpanded] = useState(false);
  const [subscribersExpanded, setSubscribersExpanded] = useState(false);
  const [expiredStatusFilter, setExpiredStatusFilter] = useState<'all' | 'active' | 'expired' | 'overdue'>('all');
  const [subscribersLoaded, setSubscribersLoaded] = useState(false);
  const [subscribersLoading, setSubscribersLoading] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const fetchPremiumData = async () => {
      setLoading(true);
      try {
        const data = await fetchPremiumStatusData(timePeriod as 'day' | 'week' | 'month');
        setPremiumMetrics(data);
        setLastUpdated(new Date());
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch premium status data';
        setError(errorMessage);
        console.error('Error fetching premium status data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPremiumData();

    // Subscribe to real-time updates
    const unsubscribe = subscribeToPremiumStatusUpdates(
      (data) => {
        setPremiumMetrics(data);
        setLastUpdated(new Date());
      },
      timePeriod as 'day' | 'week' | 'month'
    );

    setRecentExpanded(false);

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [timePeriod]);

  useEffect(() => {
    fetchSubscribers();
  }, []);

  const fetchSubscribers = async () => {
    setSubscribersLoading(true);
    try {
      const response = await fetch('https://api.notely.se/dashboard/subscribers');
      if (response.ok) {
        const data: PremiumStatus[] = await response.json();
        setSubscribers(data);
        setSubscribersLoaded(true);
      }
    } catch (err) {
      console.error('Error fetching subscribers:', err);
    } finally {
      setSubscribersLoading(false);
    }
  };

  const getRecentUpdatesWithEmail = (): PremiumStatus[] => {
    if (!premiumMetrics) return [];
    const subscriberMap = new Map(subscribers.map(s => [s.uuid, s]));
    return premiumMetrics.recentUpdates.map(update => ({
      ...update,
      email: subscriberMap.get(update.uuid)?.email,
      name: subscriberMap.get(update.uuid)?.name,
      lastEmailSentAt: subscriberMap.get(update.uuid)?.lastEmailSentAt ?? update.lastEmailSentAt,
      lastEmailType: subscriberMap.get(update.uuid)?.lastEmailType ?? update.lastEmailType,
    }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-900">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-gray-900 min-h-screen">
        <Alert variant="destructive" className="m-4">
          <AlertCircle className="h-5 w-5" />
          <AlertDescription>Error: {error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!premiumMetrics) {
    return (
      <div className="p-4 bg-gray-900 min-h-screen">
        <Alert className="m-4">
          <AlertDescription>No premium status data available</AlertDescription>
        </Alert>
      </div>
    );
  }

  // Prepare data for Premium Status pie chart
  const pieChartData = [
    { name: 'Active', value: premiumMetrics.activeUsers, color: '#10B981' },
    { name: 'Expired', value: premiumMetrics.expiredUsers, color: '#EF4444' }
  ];

  const allRecentUpdates = getRecentUpdatesWithEmail();
  const recentUpdates = recentExpanded ? allRecentUpdates : allRecentUpdates.slice(0, 5);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const allSubscribersSorted = [...subscribers]
    .sort((a, b) => new Date(a.renewalDate).getTime() - new Date(b.renewalDate).getTime());
  const filteredSubscribers = expiredStatusFilter === 'all'
    ? allSubscribersSorted
    : expiredStatusFilter === 'active'
    ? allSubscribersSorted.filter(s => s.status && new Date(s.renewalDate) >= today)
    : expiredStatusFilter === 'expired'
    ? allSubscribersSorted.filter(s => !s.status)
    : allSubscribersSorted.filter(s => s.status && new Date(s.renewalDate) < today); // overdue
  const paginatedSubscribers = subscribersExpanded ? filteredSubscribers : filteredSubscribers.slice(0, 5);

  return (
    <div className="p-4 lg:p-8 bg-gray-900 min-h-screen">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:justify-between mb-6">
        <div className="flex items-center gap-4 mb-4 lg:mb-0">
          <a
            href="/dashboard-process-logs"
            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Dashboard</span>
          </a>
          <h1 className="text-xl lg:text-3xl font-bold text-white flex items-center gap-2">
            <CreditCard className="w-6 h-6 lg:w-8 lg:h-8" />
            Subscriptions
          </h1>
        </div>
        <div className="flex items-center gap-4">
          <select
            value={timePeriod}
            onChange={(e) => setTimePeriod(e.target.value)}
            className="bg-gray-700 text-white text-sm rounded px-3 py-2"
          >
            <option value="day">Last Day</option>
            <option value="week">Last Week</option>
            <option value="month">Last Month</option>
          </select>
          <div className="text-sm text-gray-400">
            Last updated: {lastUpdated.toLocaleTimeString()}
          </div>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Card className="bg-gray-800 text-white">
          <CardContent className="p-4 lg:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Total Premium Users</p>
                <p className="text-2xl font-bold text-blue-400">{premiumMetrics.totalPremiumUsers}</p>
              </div>
              <Users className="w-8 h-8 text-gray-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gray-800 text-white">
          <CardContent className="p-4 lg:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Active Premium</p>
                <p className="text-2xl font-bold text-green-400">{premiumMetrics.activeUsers}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gray-800 text-white">
          <CardContent className="p-4 lg:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Expired Premium</p>
                <p className="text-2xl font-bold text-red-400">{premiumMetrics.expiredUsers}</p>
              </div>
              <Calendar className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-col space-y-4">
        {/* Premium Status Distribution */}
        <Card className="bg-gray-800 text-white">
          <CardHeader className="p-4 lg:p-6">
            <CardTitle className="text-lg">Premium Status Distribution</CardTitle>
          </CardHeader>
          <CardContent className="p-4 lg:p-6">
            <div className="h-80 lg:h-72 flex flex-col lg:flex-row items-center justify-around">
              <ResponsiveContainer width={isMobile ? "100%" : "50%"} height="100%">
                <PieChart>
                  <Pie
                    data={pieChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry) => `${entry.name}: ${entry.value}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {pieChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: 'none' }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-4 lg:mt-0">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-green-500 rounded"></div>
                    <span className="text-sm">Active: {premiumMetrics.activeUsers} users</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-red-500 rounded"></div>
                    <span className="text-sm">Expired: {premiumMetrics.expiredUsers} users</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Premium Users by Platform */}
        <Card className="bg-gray-800 text-white">
          <CardHeader className="p-4 lg:p-6">
            <CardTitle className="text-lg">Premium Users by Platform</CardTitle>
          </CardHeader>
          <CardContent className="p-4 lg:p-6">
            <div className="h-80 lg:h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={premiumMetrics.platformDistribution}
                  layout={isMobile ? "vertical" : "horizontal"}
                  margin={isMobile ? { left: 60 } : { bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  {isMobile ? (
                    <>
                      <YAxis
                        dataKey="platform"
                        type="category"
                        stroke="#9CA3AF"
                        tick={{ fontSize: 12 }}
                      />
                      <XAxis type="number" stroke="#9CA3AF" />
                    </>
                  ) : (
                    <>
                      <XAxis dataKey="platform" stroke="#9CA3AF" />
                      <YAxis stroke="#9CA3AF" />
                    </>
                  )}
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1F2937', border: 'none' }}
                    wrapperStyle={{ zIndex: 1000 }}
                  />
                  <Legend />
                  <Bar dataKey="count" fill="#60A5FA" name="Total Users" />
                  <Bar dataKey="activeCount" fill="#10B981" name="Active Users" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Premium Renewal Timeline */}
        <Card className="bg-gray-800 text-white">
          <CardHeader className="p-4 lg:p-6">
            <CardTitle className="text-lg">Premium Renewal Timeline</CardTitle>
          </CardHeader>
          <CardContent className="p-4 lg:p-6">
            <div className="h-80 lg:h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={premiumMetrics.renewalTimeline.slice(-15)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis
                    dataKey="date"
                    stroke="#9CA3AF"
                    angle={-45}
                    textAnchor="end"
                    height={60}
                    interval={isMobile ? 2 : 0}
                    tick={{ fontSize: isMobile ? 10 : 12 }}
                  />
                  <YAxis stroke="#9CA3AF" />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1F2937', border: 'none' }}
                    wrapperStyle={{ zIndex: 1000 }}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke="#F59E0B"
                    name="Renewals"
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Recent Premium Updates */}
        <Card className="bg-gray-800 text-white">
          <CardHeader className="p-4 lg:p-6">
            <CardTitle className="text-lg">Recent Premium Updates</CardTitle>
          </CardHeader>
          <CardContent className="p-4 lg:p-6">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-gray-400 border-b border-gray-700">
                    <th className="text-left p-2">Platform</th>
                    <th className="text-left p-2">Status</th>
                    <th className="text-left p-2">Email</th>
                    <th className="text-left p-2">Renewal Date</th>
                    <th className="text-left p-2">Updated</th>
                    <th className="text-left p-2">Last Email</th>
                    <th className="text-left p-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {recentUpdates.map((update, index) => (
                    <tr key={index} className="border-b border-gray-700">
                      <td className="p-2">{update.platform}</td>
                      <td className="p-2">
                        <span className={`px-2 py-1 rounded text-xs ${
                          update.status ? 'bg-green-900 text-green-300' : 'bg-red-900 text-red-300'
                        }`}>
                          {update.status ? 'Active' : 'Expired'}
                        </span>
                      </td>
                      <td className="p-2">
                        {update.email
                          ? <span className="text-gray-300">{update.email}</span>
                          : <span className="text-gray-500">—</span>
                        }
                      </td>
                      <td className="p-2">
                        {new Date(update.renewalDate).toLocaleDateString()}
                      </td>
                      <td className="p-2">
                        {update.updatedAt instanceof Date
                          ? update.updatedAt.toLocaleString()
                          : new Date((update.updatedAt as any).seconds * 1000).toLocaleString()
                        }
                      </td>
                      <td className="p-2 text-xs text-gray-400">
                        {update.lastEmailSentAt
                          ? <>{formatRelative(update.lastEmailSentAt)}<br/><span className="text-gray-500">{update.lastEmailType}</span></>
                          : <span className="text-gray-600">—</span>
                        }
                      </td>
                      <td className="p-2">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => {
                              setSelectedSubscriber(update);
                              setEmailModalOpen(true);
                            }}
                            className="p-1.5 text-gray-400 hover:text-blue-400 transition-colors"
                            title="Generate feedback email"
                          >
                            <Mail className="w-4 h-4" />
                          </button>
                          {update.status === false && (
                            <button
                              onClick={() => {
                                setSelectedWinBackSubscriber(update);
                                setWinBackModalOpen(true);
                              }}
                              className="p-1.5 text-gray-400 hover:text-amber-400 transition-colors"
                              title="Send win-back offer"
                            >
                              <Gift className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {allRecentUpdates.length > 5 && (
              <div className="mt-4 pt-4 border-t border-gray-700 text-center">
                <button
                  onClick={() => setRecentExpanded(e => !e)}
                  className="px-4 py-1.5 text-sm bg-gray-700 text-white rounded hover:bg-gray-600 transition-colors"
                >
                  {recentExpanded ? 'Show less' : `Show all (${allRecentUpdates.length})`}
                </button>
              </div>
            )}
          </CardContent>
        </Card>
        {/* Subscribers */}
        <Card className="bg-gray-800 border-gray-700 text-white">
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <CardTitle className="text-white text-lg">
                Subscribers{subscribersLoaded ? ` (${filteredSubscribers.length})` : ''}
              </CardTitle>
              {subscribersLoaded && (
                <div className="flex items-center gap-2">
                  {([
                    { value: 'all', label: 'All', activeClass: 'bg-gray-500 text-white' },
                    { value: 'active', label: 'Active (no overdue)', activeClass: 'bg-green-700 text-green-100' },
                    { value: 'expired', label: 'Expired', activeClass: 'bg-red-700 text-red-100' },
                    { value: 'overdue', label: 'Active (overdue)', activeClass: 'bg-amber-700 text-amber-100' },
                  ] as const).map(({ value, label, activeClass }) => (
                    <button
                      key={value}
                      onClick={() => { setExpiredStatusFilter(value); setSubscribersExpanded(false); }}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                        expiredStatusFilter === value
                          ? activeClass
                          : 'bg-gray-700 text-gray-400 hover:bg-gray-600 hover:text-white'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {!subscribersLoaded ? (
              <div className="flex flex-col items-center justify-center py-12 gap-3">
                {subscribersLoading ? (
                  <RefreshCw className="w-5 h-5 animate-spin text-blue-400" />
                ) : (
                  <button
                    onClick={fetchSubscribers}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-500 transition-colors text-sm"
                  >
                    Load Subscribers
                  </button>
                )}
              </div>
            ) : (
            <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-gray-400 border-b border-gray-700">
                    <th className="text-left p-2">Platform</th>
                    <th className="text-left p-2">Status</th>
                    <th className="text-left p-2">Email</th>
                    <th className="text-left p-2">Renewal Date</th>
                    <th className="text-left p-2">Last Email</th>
                    <th className="text-left p-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedSubscribers.map((subscriber, index) => (
                    <tr key={index} className="border-b border-gray-700">
                      <td className="p-2">{subscriber.platform}</td>
                      <td className="p-2">
                        <span className={`px-2 py-1 rounded text-xs ${
                          subscriber.status ? 'bg-green-900 text-green-300' : 'bg-red-900 text-red-300'
                        }`}>
                          {subscriber.status ? 'Active' : 'Expired'}
                        </span>
                      </td>
                      <td className="p-2">
                        {subscriber.email
                          ? <span className="text-gray-300">{subscriber.email}</span>
                          : <span className="text-gray-500">—</span>
                        }
                      </td>
                      <td className="p-2">
                        {new Date(subscriber.renewalDate).toLocaleDateString()}
                      </td>
                      <td className="p-2 text-xs text-gray-400">
                        {subscriber.lastEmailSentAt
                          ? <>{formatRelative(subscriber.lastEmailSentAt)}<br/><span className="text-gray-500">{subscriber.lastEmailType}</span></>
                          : <span className="text-gray-600">—</span>
                        }
                      </td>
                      <td className="p-2">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => {
                              setSelectedSubscriber(subscriber);
                              setEmailModalOpen(true);
                            }}
                            className="p-1.5 text-gray-400 hover:text-blue-400 transition-colors"
                            title="Generate feedback email"
                          >
                            <Mail className="w-4 h-4" />
                          </button>
                          {(!subscriber.status || (subscriber.status && new Date(subscriber.renewalDate) < today)) && (
                            <button
                              onClick={() => {
                                setSelectedWinBackSubscriber(subscriber);
                                setWinBackModalOpen(true);
                              }}
                              className="p-1.5 text-gray-400 hover:text-amber-400 transition-colors"
                              title="Send win-back offer"
                            >
                              <Gift className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredSubscribers.length > 5 && (
              <div className="mt-4 pt-4 border-t border-gray-700 text-center">
                <button
                  onClick={() => setSubscribersExpanded(e => !e)}
                  className="px-4 py-1.5 text-sm bg-gray-700 text-white rounded hover:bg-gray-600 transition-colors"
                >
                  {subscribersExpanded ? 'Show less' : `Show all (${filteredSubscribers.length})`}
                </button>
              </div>
            )}
            </>
            )}
          </CardContent>
        </Card>
      </div>

      <EmailTemplateModal
        isOpen={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        subscriber={selectedSubscriber}
        onSent={(uuid, type) => {
          const now = new Date().toISOString();
          setSubscribers(prev => prev.map(s => s.uuid === uuid ? { ...s, lastEmailSentAt: now, lastEmailType: type } : s));
        }}
      />
      <WinBackEmailModal
        isOpen={winBackModalOpen}
        onClose={() => setWinBackModalOpen(false)}
        subscriber={selectedWinBackSubscriber}
        onSent={(uuid, type) => {
          const now = new Date().toISOString();
          setSubscribers(prev => prev.map(s => s.uuid === uuid ? { ...s, lastEmailSentAt: now, lastEmailType: type } : s));
        }}
      />
    </div>
  );
};

export default Subscriptions;
